import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { TrendingUp, Users, Award, ArrowRight, BarChart as ChartBar, Zap, Globe } from 'lucide-react';

const caseStudies = [
  {
    id: 1,
    title: 'TechStart Inc. Digital Transformation',
    description: 'How we helped a startup achieve 300% growth in digital presence',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&h=500',
    results: [
      { metric: '300%', label: 'Growth in Leads' },
      { metric: '150%', label: 'Revenue Increase' },
      { metric: '5x', label: 'ROI' }
    ],
    industry: 'Technology',
    icon: Zap
  },
  {
    id: 2,
    title: 'HealthPlus Marketing Revolution',
    description: 'Transforming healthcare marketing with data-driven strategies',
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=800&h=500',
    results: [
      { metric: '200%', label: 'Patient Inquiries' },
      { metric: '85%', label: 'Cost Reduction' },
      { metric: '4.8/5', label: 'Patient Satisfaction' }
    ],
    industry: 'Healthcare',
    icon: Globe
  },
  {
    id: 3,
    title: 'FinanceHub Digital Success',
    description: 'Revolutionizing financial services through digital innovation',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&h=500',
    results: [
      { metric: '250%', label: 'Client Acquisition' },
      { metric: '180%', label: 'Digital Engagement' },
      { metric: '3x', label: 'Market Reach' }
    ],
    industry: 'Finance',
    icon: ChartBar
  }
];

const stats = [
  {
    icon: TrendingUp,
    value: '500+',
    label: 'Projects Completed',
    color: 'text-blue-600'
  },
  {
    icon: Users,
    value: '250+',
    label: 'Happy Clients',
    color: 'text-green-600'
  },
  {
    icon: Award,
    value: '50+',
    label: 'Industry Awards',
    color: 'text-purple-600'
  }
];

export default function CaseStudies() {
  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <header className="relative overflow-hidden bg-gradient-to-r from-blue-600 to-blue-800 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Client Success Stories
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
              Real results, real impact. Discover how we've helped businesses achieve their goals.
            </p>
          </motion.div>
        </div>
      </header>

      {/* Stats Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center justify-center p-6 bg-gray-50 rounded-2xl"
              >
                <stat.icon className={`w-8 h-8 ${stat.color} mr-4`} />
                <div>
                  <div className="text-3xl font-bold text-gray-900">{stat.value}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {caseStudies.map((study, index) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                <div className="relative">
                  <img
                    src={study.image}
                    alt={study.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full p-2">
                    <study.icon className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <div className="p-6">
                  <div className="text-sm font-medium text-blue-600 mb-2">{study.industry}</div>
                  <h2 className="text-xl font-bold text-gray-900 mb-3">{study.title}</h2>
                  <p className="text-gray-600 mb-6">{study.description}</p>
                  
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {study.results.map((result, idx) => (
                      <div key={idx} className="text-center">
                        <div className="text-xl font-bold text-gray-900">{result.metric}</div>
                        <div className="text-sm text-gray-600">{result.label}</div>
                      </div>
                    ))}
                  </div>

                  <Link
                    to={`/case-studies/${study.id}`}
                    className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
                  >
                    View Case Study
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}